import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { json } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './pledge-slot-field.css'

// COMPONENTS
import * as Icons from '../../../_constants/icons'
import { Column } from '@components/templates/column'
import CurrencyTextField from '@components/molecules/currency-textfield'
import TextField from '@components/atoms/textfield'
import Button from '@components/atoms/button'
import Spinner from '@components/atoms/spinner'

// UTILITIES
import * as GroupActions from '../../../routes/group/_localStore/actions'
import { min, max } from '@constants'
import { flatten } from '@utils/string-helper'
import { toPennies } from '@utils/number-helper'
import { parsePledge } from '@schemas'
import { toUSD, useCurrencyInput, useInput } from '@hooks/inputs'
import { isNonEmptyString, isNumber } from '@utils/type-checkers'
import { createResponseObj, VirtualCardAPI } from '@apis'
import { useGroupDispatch, useGroupSettings } from '../../../routes/group/_localStore/_hooks'


interface Props extends React.HTMLAttributes<HTMLFieldSetElement> {
    isEdit?: boolean
    group: Group
    pledge?: Pledge
    showRemove?: boolean
    onPledgeAction?: () => void
    onPledgeSubmit?: () => void
    onPledgeUpdate?: () => void
    onPledgeRemove?: () => void
}

const PledgeSlotFields: FC<Props> = (props) => {
    const { className: classProps, isEdit, group, pledge, showRemove, onPledgeAction, onPledgeUpdate, onPledgeSubmit, onPledgeRemove, ...fieldsetProps } = props
    const className = flatten("pledge-slot-fields", "flex flex-row items-stretch", classProps)
    const { isSignedIn, user } = useUser()
    const [hide, setHide] = useState(true)
    const [addingPledge, setAddingPledge] = useState(false)
    const [updatingPledge, setUpdatingPledge] = useState(false)
    const [removingPledge, setRemovingPledge] = useState(false)
    const [focus, setFocus] = useState(true)

    const localDispatch = useGroupDispatch()
    const { state } = useGroupSettings()
    const { isEditingParticipant } = state

    const [name, pledgeNameProps] = useInput({
        initValue: pledge?.name || ''
    })
    const [amount, pledgeAmountProps] = useCurrencyInput({
        initValue: !!pledge?.amount ? toUSD(`${(pledge.amount) / 100}`, undefined, undefined, true) : '0', max,
    })
    const pledgeSubmitable = useMemo(() => (toPennies(amount) >= min && toPennies(amount) <= max), [amount])

    const { pledges = [] } = group || undefined
    const { openPledges } = useMemo(() => pledges.reduce<{ userPledges: Pledge[], claimedPledges: Pledge[], openPledges: Pledge[] }>((prev, pledge) => {
        if (isSignedIn && isNonEmptyString(user.id) && pledge.user?.id === user.id) return { ...prev, userPledges: [...prev.userPledges, pledge] }
        else if (!!pledge.transactionId || !!pledge.user?.id) return { ...prev, claimedPledges: [...prev.claimedPledges, pledge] }
        else return { ...prev, openPledges: [...prev.openPledges, pledge] }
    }, { userPledges: [], claimedPledges: [], openPledges: [] }), [isSignedIn, user?.id, pledges])

    useEffect(() => {
        (isEdit || isEditingParticipant) ? setHide(false) : setHide(true)
    }, [isEdit, isEditingParticipant])

    useEffect(() => {
        if (openPledges.length > 0) {
            localDispatch(GroupActions.setEditingParticipant(true))
            setFocus(true)
        }
        else setFocus(false)
    }, [openPledges])

    const amountInputRef = useRef<HTMLInputElement>(null)
    const ref = useRef<HTMLDialogElement>(null)
    function openRemoveConfirm() { if (ref.current) ref.current.showModal() }
    function closeRemoveConfirm() { if (ref.current) ref.current.close() }

    function handleAction() {
        setHide(false)
        !!onPledgeAction && onPledgeAction()
    }

    function handleRemove() {        
        if (!pledge) {
            localDispatch(GroupActions.setEditingParticipant(false))
            !!onPledgeRemove && onPledgeRemove()
            return
        } else openRemoveConfirm()
    }

    async function removePledgeSlot() {
        closeRemoveConfirm()
        setRemovingPledge(true)

        const groupId = group?.id
        const pledgeId = pledge?.id

        console.log("Remove Pledge Data:", { groupId, pledgeId })
        if (!isNonEmptyString(groupId) || !isNonEmptyString(pledgeId)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify({ groupId, pledgeId })}`
        })

        await VirtualCardAPI.RemovePledgeRequest.fetch(groupId, pledgeId)
        !!onPledgeRemove && onPledgeRemove()
        setRemovingPledge(false)
        localDispatch(GroupActions.setEditingParticipant(false))
        localDispatch(GroupActions.setRemovePledge(pledge?.id))
        return json('success', { status: 200 })
    }

    async function submitPledgeSlot() {
        setAddingPledge(true)
        let groupId = group.id

        console.log("Add Pledge Data:", { groupId, name, amount })
        if (!isNonEmptyString(groupId)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify(groupId)}`
        })

        await VirtualCardAPI.AddPledgeRequest.fetch(groupId, parsePledge({
            name: `${name}`,
            amount: toPennies(amount),
        }))

        !!onPledgeSubmit && onPledgeSubmit()
        setAddingPledge(false)
        return json('success', { status: 200 })
    }

    async function updatePledgeSlot() {
        setUpdatingPledge(true)
        const groupId = group?.id
        const pledgeId = pledge?.id
        const _amount = toPennies(amount)

        console.log("Update Pledge Data:", { groupId, pledgeId, _amount })
        if (!isNonEmptyString(groupId) || !isNonEmptyString(pledgeId) || (!isNumber(_amount) || _amount < 100)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify({ groupId, pledgeId, _amount })}`
        })

        if (name !== (pledge?.name || '') || _amount !== (pledge?.amount || 0))
            await VirtualCardAPI.UpdatePledgeRequest.fetch(groupId, { name, id: pledgeId, amount: _amount })

        !!onPledgeUpdate && onPledgeUpdate()
        setUpdatingPledge(false)
        localDispatch(GroupActions.setEditingParticipant(false))
        return json('success', { status: 200 })
    }

    return (<>
        <fieldset name="pledge" className={className} {...fieldsetProps}>
            {(addingPledge || updatingPledge || removingPledge) ? (
                <div className="pledge-loader" style={{ zIndex: 2, borderRadius: 'inherit' }}>
                    <Spinner indicator='circle' />
                </div>
            ) : (<>
                {(!hide) &&
                    <div className="remove" onClick={handleRemove}>
                        <Icons.Close height="10px" width="10px" tint="var(--splitcard-black)" strokeWidth="3" />
                    </div>}
                <div className='input-row'>
                    <TextField
                        className='name-input'
                        {...pledgeNameProps}
                        focus={focus}
                        onClick={handleAction}
                        name="name"
                        placeholder='Enter Name'
                        enterKeyHint="next"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                e.stopPropagation()
                                amountInputRef.current?.focus()
                            }
                        }}
                    />
                    <CurrencyTextField
                        className='amount-input'
                        ref={amountInputRef}
                        {...pledgeAmountProps}
                        onClick={handleAction}
                        name="amount"
                    />
                </div>
            </>)}
        </fieldset>
        <dialog ref={ref}>
            <div>
                <div className="flex flex-col items-stretch" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem', background: 'var(--splitcard-white)', gap: '1rem', padding: '1.3125rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                    <h2>Remove Participant</h2>
                    <p>Their name and amount will not show in the list when they go to join the group. Users can still join with their own custom amount.</p>
                    <div className="flex flex-row gap-2.5">
                        <Button theme="light" style={{ flex: 1 }} onClick={closeRemoveConfirm}>Cancel</Button>
                        <Button theme="primary" style={{ flex: 1 }} onClick={removePledgeSlot}>Remove</Button>
                    </div>
                </div>
            </div>
        </dialog>
        {!hide && <Column.Footer>
            {!isEdit && !!onPledgeSubmit && <Button theme='primary' disabled={!pledgeSubmitable} onClick={submitPledgeSlot}>Invite</Button>}
            {!!isEdit && !!onPledgeUpdate && <Button theme='primary' disabled={!pledgeSubmitable} onClick={updatePledgeSlot}>Update</Button>}
        </Column.Footer>}
    </>)
}

export default PledgeSlotFields
